<div class="w-full max-w-full">
  
  <!-- Header -->
  <div class="mb-6">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Ogłoszenia systemowe</h1>
        <p class="text-gray-600 dark:text-gray-400 mt-1">Zarządzaj komunikatami dla użytkowników</p>
      </div>
      <button 
        mat-raised-button 
        color="primary"
        (click)="navigateToNew()"
        class="!rounded-lg">
        <mat-icon [svgIcon]="'tabler_outline:plus'" class="mr-2"></mat-icon>
        Dodaj ogłoszenie
      </button>
    </div>
  </div>

  <!-- Loading state -->
  <div *ngIf="isLoading" class="flex items-center justify-center py-20">
    <mat-spinner diameter="50"></mat-spinner>
  </div>

  <!-- Content -->
  <ng-container *ngIf="!isLoading">

    <!-- Active Announcements - Highlighted Section -->
    <div *ngIf="activeAnnouncements.length > 0" class="mb-6">
      <div class="bg-gradient-to-br from-green-500 to-green-700 rounded-2xl shadow-xl p-6 text-white">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <div class="p-3 bg-white/20 backdrop-blur-sm rounded-full">
              <mat-icon class="text-white text-3xl" [svgIcon]="'tabler_outline:bell-ringing'"></mat-icon>
            </div>
            <div>
              <h2 class="text-2xl font-bold">Aktywne ogłoszenia</h2>
              <p class="text-sm text-white/90 mt-1">
                Obecnie wyświetlane użytkownikom · 
                <span class="font-semibold">{{ activeAnnouncements.length }} {{ activeAnnouncements.length === 1 ? 'ogłoszenie' : 'ogłoszenia' }}</span>
              </p>
            </div>
          </div>
          <div class="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full">
            <span class="text-2xl font-bold">{{ activeAnnouncements.length }}</span>
          </div>
        </div>

        <!-- Active announcements list -->
        <div class="space-y-3">
          <div 
            *ngFor="let announcement of activeAnnouncements; let i = index"
            class="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all cursor-pointer"
            (click)="navigateToEdit(announcement._id)">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-sm font-mono text-white/70">#{{ i + 1 }}</span>
                  <h3 class="text-lg font-semibold text-white">{{ announcement.title }}</h3>
                </div>
                <p class="text-sm text-white/80 line-clamp-2">{{ announcement.description }}</p>
              </div>
              <button 
                mat-icon-button
                class="!text-white"
                matTooltip="Edytuj ogłoszenie">
                <mat-icon [svgIcon]="'tabler_outline:edit'"></mat-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state for active announcements -->
    <div *ngIf="activeAnnouncements.length === 0" class="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-lg p-6 mb-6">
      <div class="flex items-center gap-3">
        <mat-icon class="text-amber-600 dark:text-amber-400 text-3xl" [svgIcon]="'tabler_outline:alert-triangle'"></mat-icon>
        <div>
          <h3 class="text-lg font-semibold text-amber-900 dark:text-amber-200">Brak aktywnych ogłoszeń</h3>
          <p class="text-sm text-amber-700 dark:text-amber-300 mt-1">
            Aktualnie żadne ogłoszenia nie są wyświetlane użytkownikom
          </p>
        </div>
      </div>
    </div>

    <!-- All Announcements Table -->
    <div class="bg-card rounded-2xl shadow-lg overflow-hidden">
      
      <!-- Table header -->
      <div class="p-6 border-b dark:border-gray-700">
        <div class="flex items-center gap-3">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Wszystkie ogłoszenia</h2>
          <span class="px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
            {{ announcements.length }}
          </span>
        </div>
      </div>

      <!-- Empty state -->
      <div *ngIf="announcements.length === 0" class="p-12 text-center">
        <mat-icon class="text-6xl text-gray-400 mb-4" [svgIcon]="'tabler_outline:speakerphone'"></mat-icon>
        <h3 class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Brak ogłoszeń
        </h3>
        <p class="text-gray-500 dark:text-gray-400 mb-6">
          Utwórz pierwsze ogłoszenie systemowe dla użytkowników
        </p>
        <button 
          mat-raised-button 
          color="primary"
          (click)="navigateToNew()"
          class="!rounded-lg">
          <mat-icon [svgIcon]="'tabler_outline:plus'" class="mr-2"></mat-icon>
          Dodaj ogłoszenie
        </button>
      </div>

      <!-- Table -->
      <div *ngIf="announcements.length > 0" class="overflow-x-auto">
        <table mat-table [dataSource]="announcements" class="w-full">

          <!-- Number Column -->
          <ng-container matColumnDef="number">
            <th mat-header-cell *matHeaderCellDef class="font-semibold w-20">#</th>
            <td mat-cell *matCellDef="let announcement; let i = index">
              <span class="text-gray-500 dark:text-gray-400 font-mono text-sm">
                {{ i + 1 }}
              </span>
            </td>
          </ng-container>

          <!-- Title Column -->
          <ng-container matColumnDef="title">
            <th mat-header-cell *matHeaderCellDef class="font-semibold">Tytuł ogłoszenia</th>
            <td mat-cell *matCellDef="let announcement">
              <div>
                <div class="font-medium text-gray-900 dark:text-white mb-1">
                  {{ announcement.title }}
                </div>
                <div class="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                  {{ announcement.description }}
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef class="font-semibold w-40">Status</th>
            <td mat-cell *matCellDef="let announcement">
              <span 
                class="px-3 py-1 rounded-full text-xs font-semibold"
                [ngClass]="{
                  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400': announcement.isActive,
                  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300': !announcement.isActive
                }">
                <mat-icon 
                  class="text-xs mr-1" 
                  [svgIcon]="announcement.isActive ? 'tabler_outline:circle-check' : 'tabler_outline:circle-x'"
                  [ngClass]="{
                    'text-green-600 dark:text-green-400': announcement.isActive,
                    'text-gray-500 dark:text-gray-400': !announcement.isActive
                  }">
                </mat-icon>
                {{ announcement.isActive ? 'Aktywne' : 'Nieaktywne' }}
              </span>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="font-semibold text-right w-16"></th>
            <td mat-cell *matCellDef="let announcement" class="text-right" (click)="$event.stopPropagation()">
              <button 
                mat-icon-button
                color="primary"
                (click)="navigateToEdit(announcement._id)"
                matTooltip="Edytuj ogłoszenie"
                class="!text-primary-600 dark:!text-primary-400">
                <mat-icon [svgIcon]="'tabler_outline:edit'"></mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr 
            mat-row 
            *matRowDef="let row; columns: displayedColumns;" 
            (click)="navigateToEdit(row._id)"
            class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"></tr>
        </table>
      </div>

    </div>

  </ng-container>

</div>